import { objectMovingConstrainCallback } from '@/infrastructure/engine2d/FabricCallbacks';
import { CANVAS_FRAME_BACKGROUND_COLOR } from '@/infrastructure/engine2d/engine2dConstants';
import { FabricSceneEventsService } from '@/infrastructure/engine2d/events/FabricSceneEventsService';
import { PanController } from '@/infrastructure/engine2d/input/PanController';
import { TouchController } from '@/infrastructure/engine2d/input/TouchController';
import { ZoomController } from '@/infrastructure/engine2d/input/ZoomController';
import { computeDefaultZoomFactor, computeZoomBounds } from '@/utils/zoomFactors';
import { Canvas, Point } from 'fabric';
import type { IEngine2DInitParams } from '@/application/ports/IEngine2DInitParams';
import type { TViewportConstraints } from '@/utils/TViewportConstraints';
import type { IFabricTextCustom } from '@/infrastructure/engine2d/types/IFabricTextCustom';
import type { IFabricImageCustom } from '@/infrastructure/engine2d/types/IFabricImageCustom';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';
import type { TFabricCustomSelectable } from '@/infrastructure/engine2d/types/TFabricCustomSelectable';

type TResizeListener = (width: number, height: number) => void;

// Bare fabric canvas core: owns the Canvas + its intrinsic dimensions,
// pointer input controllers (zoom / pan / touch), the non-overlay scene
// events (object constraint + contextual menu), and the resize event
// bus. Overlay concerns (grid, mask, cut fields, viewport fit-to-view)
// live in FabricViewportEngine2D and FabricPrintZoneEngine2D — the
// scene emits a resize event so the viewport engine can rescale
// without being called into.
class FabricScene {
    readonly fabricCanvas: Canvas;
    canvasWidth: number = 0;
    canvasHeight: number = 0;
    canvasBackgroundColor: string = 'transparent';

    private readonly _width: number;
    private readonly _height: number;
    private readonly _panInput: PanController;
    private readonly _sceneEvents: FabricSceneEventsService;
    private readonly _touchInput: TouchController;
    private readonly _zoomInput: ZoomController;
    // No-op default; FabricEngine2D replaces it via setZoomNotifier.
    private _notifyZoom: (zoom: number) => void = () => {};
    private _clickRefPoint: Point | null = null;
    private _resizeListeners: TResizeListener[] = [];

    get imageRenderWidth() {
        return this._width;
    }

    get imageRenderHeight() {
        return this._height;
    }

    // Zoom of the default view: the print surface spanning
    // ZOOM_DEFAULT_SURFACE_RATIO of its tighter axis. Derived on every read
    // rather than cached, because the canvas size changes on resize and a
    // stored copy would silently go stale — which is exactly what happened to
    // the field this replaces.
    get defaultZoomFactor(): number {
        return computeDefaultZoomFactor(this._zoomFactorParams);
    }

    // Single source of gesture constraints for the input controllers.
    get viewportConstraints(): TViewportConstraints {
        return {
            contentHeight: this.imageRenderHeight,
            contentWidth: this.imageRenderWidth,
            zoomBounds: computeZoomBounds(this._zoomFactorParams),
        };
    }

    private get _zoomFactorParams() {
        return {
            canvasHeight: this.canvasHeight,
            canvasWidth: this.canvasWidth,
            surfaceHeight: this.imageRenderHeight,
            surfaceWidth: this.imageRenderWidth,
        };
    }

    constructor(canvas: HTMLCanvasElement, params: IEngine2DInitParams) {
        this._width = params.width;
        this._height = params.height;
        this.canvasWidth = Math.floor(canvas.parentElement?.getBoundingClientRect().width ?? canvas.width);
        this.canvasHeight = Math.floor(canvas.parentElement?.getBoundingClientRect().height ?? canvas.height);
        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;
        // Fabric-internal options that were previously duplicated at every
        // call site — none of them vary across the app, so they live here
        // rather than leaking through the engine-neutral port.
        this.fabricCanvas = new Canvas(canvas, {
            absolutePan: true,
            allowTouchScrolling: false,
            backgroundColor: CANVAS_FRAME_BACKGROUND_COLOR,
            enableRetinaScaling: true,
            includeDefaultValues: false,
            preserveObjectStacking: true,
            selection: false,
        });

        // Closure so later setZoomNotifier calls take effect without
        // re-instantiating the input controllers.
        const notifyZoom = (zoom: number) => this._notifyZoom(zoom);
        const getConstraints = () => this.viewportConstraints;
        this._zoomInput = new ZoomController(this.fabricCanvas, notifyZoom, getConstraints);
        this._panInput = new PanController({
            canvas: this.fabricCanvas,
            getClickedRefPoint: this.getClickedRefPoint,
            notifyZoom,
            setClickedActiveObject: this.setClickedActiveObject,
            setClickedRefPoint: this.setClickedRefPoint,
            getConstraints,
        });
        this._touchInput = new TouchController({
            canvas: this.fabricCanvas,
            onViewportResize: (width, height) => this.resize(width, height),
            panCallback: this._panInput.getPanCallback(),
        });
        const objectMovingConstrain = objectMovingConstrainCallback({
            tl: new Point(),
            br: new Point({ x: this.imageRenderWidth, y: this.imageRenderHeight }),
        });
        this._sceneEvents = new FabricSceneEventsService({
            canvas: this.fabricCanvas,
            objectMovingConstrain,
        });
    }

    dispose(): void {
        this._sceneEvents.dispose();
        this._touchInput.dispose();
        this._panInput.dispose();
        this._zoomInput.dispose();
        this.fabricCanvas.dispose();
        this._resizeListeners.length = 0;
    }

    // Public resize event — overlay engine subscribes to react to size
    // changes (recompute focus / fit-to-view). Returns an unsubscribe.
    onResize(listener: TResizeListener): () => void {
        this._resizeListeners.push(listener);
        return () => {
            this._resizeListeners = this._resizeListeners.filter((l) => l !== listener);
        };
    }

    setClickedRefPoint = (point: Point | null) => {
        this._clickRefPoint = point;
    };

    getClickedRefPoint = () => {
        return this._clickRefPoint;
    };

    // Stub kept for the PanController params shape. Selection now flows
    // through fabric's native selection:* events (FabricEventHandler).
    setClickedActiveObject = (_object: TFabricCustomSelectable) => {};

    setZoomNotifier(notifier: (zoom: number) => void): void {
        this._notifyZoom = notifier;
    }

    getFabricElementById(id: string): TFabricCustomSelectable {
        return this.fabricCanvas.getObjects().find((obj) => {
            if ((obj as IFabricImageCustom).id === id) {
                return obj as IFabricImageCustom;
            }
            if ((obj as IFabricTextCustom).id === id) {
                return obj as IFabricTextCustom;
            }
            if ((obj as ITextboxCustom).id === id) {
                return obj as ITextboxCustom;
            }
            return null;
        }) as TFabricCustomSelectable;
    }

    removeFabricElementById(id: string) {
        const object = this.getFabricElementById(id);
        if (object) {
            object.hasControls = false;
            this.fabricCanvas.renderAll();
            this.fabricCanvas.remove(object);
            object.dispose();
        }
    }

    // Clamps rather than refusing: the previous `if (out of range) return`
    // made every caller a silent no-op as soon as the requested zoom left the
    // bounds, so the wheel did nothing at all at the default framing.
    zoomInOut(zoomFactor: number) {
        const { max, min } = this.viewportConstraints.zoomBounds;
        const clamped = Math.min(Math.max(zoomFactor, min), max);
        this.fabricCanvas.zoomToPoint(this.fabricCanvas.getCenterPoint(), clamped);
    }

    resize(width: number, height: number) {
        this.canvasWidth = width;
        this.canvasHeight = height;

        this.fabricCanvas.setDimensions({ width, height });

        const canvasElement = this.fabricCanvas.getElement();
        canvasElement.style.width = `${width}px`;
        canvasElement.style.height = `${height}px`;

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
        if (isIOS) {
            this._setVisualViewPortOffset();
        }

        // Notify subscribers (overlay engine handles the viewport fit).
        this._resizeListeners.forEach((listener) => listener(width, height));

        this.fabricCanvas.renderAll();
    }

    private _setVisualViewPortOffset(): void {
        if (!window.visualViewport) {
            return;
        }
        const { height } = window.visualViewport;
        const canvasElement = this.fabricCanvas.getElement();
        const rect = canvasElement.getBoundingClientRect();
        const topOffset = rect.top + height / 2;
        const outerCanvasContainer = document.getElementsByClassName('canvas-container')[0] as HTMLElement;
        if (!outerCanvasContainer) {
            return;
        }
        if (topOffset < 450) {
            if (!canvasElement.style.top || canvasElement.style.top === '0px') {
                outerCanvasContainer.style.setProperty('margin-top', `${topOffset}px`);
            }
        } else {
            outerCanvasContainer.style.setProperty('margin-top', `${0}px`);
        }
    }
}

export { FabricScene };
